import React, { useEffect, useRef, useState } from 'react';
import { MicIcon, SquareIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
}
function recognitionConstructor(): (new () => SpeechRecognitionLike) | null {
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Voice notes with live transcription through the browser's speech recognition.
 * When the device has no recogniser, the recording still saves and the
 * attachment is marked unsupported — never labelled as transcribed.
 */
export function VoiceRecorder({
  onSave
}: {
  onSave: (result: {
    durationSeconds: number;
    transcript: string;
    supported: boolean;
  }) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const recognition = useRef<SpeechRecognitionLike | null>(null);
  const timer = useRef<number | null>(null);
  const supported = recognitionConstructor() !== null;
  useEffect(() => () => {
    if (timer.current) window.clearInterval(timer.current);
    recognition.current?.stop();
  }, []);
  function start() {
    setSeconds(0);
    setTranscript('');
    setInterim('');
    setRecording(true);
    timer.current = window.setInterval(() => setSeconds(value => value + 1), 1000);
    const Recognition = recognitionConstructor();
    if (!Recognition) return;
    const instance = new Recognition();
    instance.continuous = true;
    instance.interimResults = true;
    instance.lang = 'en-NG';
    instance.onresult = (event: any) => {
      let settled = '';
      let pending = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result.isFinal) settled += `${result[0].transcript} `;else pending += result[0].transcript;
      }
      if (settled) setTranscript(current => `${current}${settled}`);
      setInterim(pending);
    };
    instance.onerror = () => setInterim('');
    instance.onend = () => setInterim('');
    try {
      instance.start();
      recognition.current = instance;
    } catch {
      recognition.current = null;
    }
  }
  function stop() {
    setRecording(false);
    if (timer.current) window.clearInterval(timer.current);
    recognition.current?.stop();
    recognition.current = null;
    const finalText = `${transcript}${interim}`.trim();
    onSave({
      durationSeconds: Math.max(seconds, 1),
      transcript: finalText,
      supported
    });
    setTranscript('');
    setInterim('');
    setSeconds(0);
  }
  return <div className={recording ? 'w-full' : 'inline-flex'} data-id="element-3916" data-mp="2d38ypojc8on8">
      <Button size="sm" variant={recording ? 'primary' : 'secondary'} icon={recording ? <SquareIcon className="h-3.5 w-3.5" data-id="element-3918" data-mp="sils539bfw13" /> : <MicIcon className="h-3.5 w-3.5" data-id="element-3919" data-mp="xbex9s8p56dp" />} onClick={() => recording ? stop() : start()} data-id="element-3917" data-mp="2d0mrjq0d0b5j">
        {recording ? `Stop recording · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : 'Record voice note'}
      </Button>

      {recording && <div className="mt-2 rounded-lg border border-line bg-surface px-3.5 py-3" data-id="element-3920" data-mp="1bamsyb5nhwd5">
          <div className="flex items-center gap-2" data-id="element-3921" data-mp="1gaizah1tlzvv">
            <span className="flex h-2 w-2 animate-pulse rounded-full bg-danger-700" aria-hidden="true" data-id="element-3922" data-mp="1g97ucdwn2m36" />
            <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3923" data-mp="12cauxo2gfwao">
              Transcribing live
            </p>
            {!supported && <Badge tone="warn" data-id="element-3924" data-mp="3mze2m2k6j4lo">No recogniser on this device</Badge>}
          </div>
          <p className="mt-1.5 min-h-[2.5rem] text-[13px] leading-6 text-ink" data-id="element-3925" data-mp="3r2k8va7akvs5">
            {transcript}
            <span className="text-ink-subtle" data-id="element-3926" data-mp="b42lq8lzfwbg">{interim}</span>
            {!transcript && !interim && <span className="text-ink-subtle" data-id="element-3927" data-mp="6ct1jnj7dl6y">
                {supported ? 'Start speaking…' : 'Audio is being captured; text will not be produced here.'}
              </span>}
          </p>
        </div>}
    </div>;
}